import React, { useMemo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  backboneLabel,
  operationBadgeClass,
  operationBadgeText,
  type FlowAgentPersistedData,
} from '../agentWorkflowTypes';
import './AgentNode.css';

export type AgentNodeData = FlowAgentPersistedData & {
  incomingInputHint: string;
};

const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

function circledNum(i: number): string {
  return CIRCLED[i] ?? `${i + 1}`;
}

function truncateObjective(text: string, maxLines = 2, maxChars = 120): string {
  const lines = text.trim().split(/\n+/);
  const two = lines.slice(0, maxLines).join(' ');
  const t = two.replace(/\s+/g, ' ').trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, maxChars - 1)}…`;
}

function schemaPreview(json: string): string {
  try {
    const o = JSON.parse(json) as Record<string, unknown>;
    if (!o || typeof o !== 'object') return '{ }';
    const inner = Object.entries(o)
      .slice(0, 5)
      .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join(', ');
    return `{ ${inner}${Object.keys(o).length > 5 ? ', …' : ''} }`;
  } catch {
    return '{ … }';
  }
}

export default function AgentNode({ data, selected }: NodeProps<AgentNodeData>) {
  const status = data.runStatus ?? 'idle';
  const preview = useMemo(() => truncateObjective(data.objective), [data.objective]);
  const outPrev = useMemo(() => schemaPreview(data.outputSchemaJson), [data.outputSchemaJson]);

  return (
    <div
      className={`agent-node${selected ? ' agent-node--selected' : ''}${status !== 'idle' ? ` agent-node--${status}` : ''}`}
      data-run-state={status}
    >
      <Handle type="target" position={Position.Left} id="in" className="agent-node__handle agent-node__handle--target" />

      <div className="agent-node__header">
        <span className="agent-node__dot" aria-hidden />
        <div className="agent-node__head-text">
          <div className="agent-node__title">{data.label}</div>
          <div className="agent-node__subtitle">Agentic workflow</div>
        </div>
        <div className="agent-node__llm-pill" title="LLM backbone">
          {backboneLabel(data.backbone)}
        </div>
      </div>

      <div className="agent-node__body nodrag">
        {status === 'running' && <div className="agent-node__status agent-node__status--running" aria-hidden />}
        {status === 'success' && <span className="agent-node__status agent-node__status--ok">✓</span>}
        {status === 'failed' && <span className="agent-node__status agent-node__status--bad">✕</span>}

        <div className="agent-node__section-label">Objective:</div>
        <div className="agent-node__objective-box">{preview || '—'}</div>

        <div className="agent-node__input-line">
          <span className="agent-node__io-label">Input</span>
          <span className="agent-node__io-arrow">←</span>
          <span className="agent-node__io-value">{data.inputMappingOverride || data.incomingInputHint}</span>
        </div>

        <div className="agent-node__section-label">Operations:</div>
        <div className="agent-node__ops">
          {data.operations.map((op, i) => (
            <div
              key={op.id}
              className={`agent-node__op-row${op.kind === 'emit' ? ' agent-node__op-row--emit' : ''}`}
            >
              <span className="agent-node__op-num">{circledNum(i)}</span>
              <span className="agent-node__op-name">{op.name}</span>
              <span className={`agent-node__badge ${operationBadgeClass(op.kind)}`}>{operationBadgeText(op.kind)}</span>
            </div>
          ))}
        </div>

        <div className="agent-node__output-line">
          <span className="agent-node__io-label">Output</span>
          <span className="agent-node__io-arrow">→</span>
          <span className="agent-node__io-value agent-node__io-value--schema">{outPrev}</span>
        </div>
      </div>

      <Handle type="source" position={Position.Right} id="out" className="agent-node__handle agent-node__handle--source" />
    </div>
  );
}
